'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useChat } from '@/lib/hooks/useChat';
import type {
  QBStatusResponse,
  QBReportResponse,
  QBProfitLossReport,
  QBARAgingDetailReport,
  QBAPAgingDetailReport,
  QBBalanceSheetReport,
  QBCashFlowReport,
  QBSalesByCustomerReport,
  QBSalesByProductReport,
  IntuitReportResponse,
  IntuitReportRow,
} from '@/lib/quickbooks/types';
import Row1Hero from './Row1Hero';
import Row2KPIStrip from './Row2KPIStrip';
import Row3PLThisMonth, { type PLPeriod } from './Row3PLThisMonth';
import Row4ARAPDetail from './Row4ARAPDetail';
import Row5CashFlowBalanceSheet from './Row5CashFlowBalanceSheet';
import Row5_5MaterialRebates from './Row5_5MaterialRebates';
import Row6TopPerformers from './Row6TopPerformers';
import AddRebateDrawer from './AddRebateDrawer';

const LOG = '[Financial]';
const BRIEFING_CACHE_KEY = 'financial_ai_briefing_cache';

// ─── Types ────────────────────────────────────────────────────────────────

interface ReportState<T> {
  data: QBReportResponse<T> | null;
  loading: boolean;
  error: string | null;
}

const emptyState = <T,>(): ReportState<T> => ({ data: null, loading: true, error: null });

// ─── QuickBooks status button (preserved from PR 1) ──────────────────────

function QuickBooksStatusButton() {
  const [status, setStatus] = useState<QBStatusResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/quickbooks/status', { signal: controller.signal })
      .then(async (res) => {
        if (!res.ok) throw new Error(`status HTTP ${res.status}`);
        const json = (await res.json()) as QBStatusResponse;
        setStatus(json);
      })
      .catch((err) => {
        if ((err as { name?: string })?.name === 'AbortError') return;
        console.warn('[QuickBooks] status fetch failed:', err);
        setStatus({ configured: false, connected: false });
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  if (loading) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold" style={{ background: '#F3F4F6', color: '#6B7280' }}>
        <span className="w-2 h-2 rounded-full bg-gray-400 animate-pulse" />
        Checking QuickBooks…
      </span>
    );
  }
  if (!status?.configured) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold"
        style={{ background: '#F3F4F6', color: '#6B7280' }}
        title="Contact ops to enable QuickBooks integration"
      >
        <Icon name="ExclamationCircleIcon" size={13} />
        QuickBooks not configured
      </span>
    );
  }
  if (!status.connected) {
    return (
      <a
        href="/api/quickbooks/connect"
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white hover:opacity-90 active:scale-95 transition-all"
        style={{ background: '#07B0E9' }}
      >
        <Icon name="LinkIcon" size={13} />
        Connect QuickBooks
      </a>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold"
      style={{ background: 'rgba(16,185,129,0.10)', color: '#047857' }}
      title={`Connected to realm ${status.realmId} (${status.environment})`}
    >
      <span className="w-2 h-2 rounded-full" style={{ background: '#10B981' }} />
      QB Connected · {status.realmId?.slice(-6) ?? ''}
    </span>
  );
}

// ─── Date helpers ────────────────────────────────────────────────────────

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function periodDates(period: PLPeriod): { start_date: string; end_date: string } {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth(); // 0-indexed
  const today = isoToday();
  if (period === 'MTD') {
    return { start_date: `${year}-${String(month + 1).padStart(2, '0')}-01`, end_date: today };
  }
  if (period === 'LastMonth') {
    const lmYear = month === 0 ? year - 1 : year;
    const lmMonth = month === 0 ? 11 : month - 1;
    const start = `${lmYear}-${String(lmMonth + 1).padStart(2, '0')}-01`;
    const endDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const end = `${lmYear}-${String(lmMonth + 1).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`;
    return { start_date: start, end_date: end };
  }
  if (period === 'QTD') {
    const qStartMonth = Math.floor(month / 3) * 3;
    return { start_date: `${year}-${String(qStartMonth + 1).padStart(2, '0')}-01`, end_date: today };
  }
  return { start_date: `${year}-01-01`, end_date: today };
}

// ─── Shared retry fetch ──────────────────────────────────────────────────

async function fetchQBReport<T>(url: string, signal: AbortSignal): Promise<QBReportResponse<T>> {
  let lastErr: unknown;
  for (let attempt = 0; attempt < 3; attempt++) {
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    try {
      const timeoutPromise = new Promise<never>((_, rej) =>
        setTimeout(() => rej(new Error('Request timed out')), 8000),
      );
      const res = await Promise.race([fetch(url, { signal }), timeoutPromise]);
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as QBReportResponse<T>;
    } catch (err) {
      lastErr = err;
      const name = (err as { name?: string })?.name;
      if (name === 'AbortError' || signal.aborted) throw err;
      const msg = err instanceof Error ? err.message : String(err);
      const isRate = msg.includes('429') || msg.toLowerCase().includes('too many');
      const isTimeout = msg.includes('timed out');
      console.warn(`${LOG} attempt ${attempt + 1} failed for ${url}: ${msg}`);
      if (attempt === 2 || (!isRate && !isTimeout)) break;
      await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt)));
    }
  }
  throw lastErr ?? new Error('Unknown error');
}

// ─── AI briefing parse helpers ───────────────────────────────────────────

function findRowValue(report: IntuitReportResponse | undefined, label: string): number {
  if (!report?.Rows?.Row) return 0;
  const walk = (rows: IntuitReportRow[]): number | null => {
    for (const row of rows) {
      const sLabel = row.Summary?.ColData?.[0]?.value;
      const sVal   = row.Summary?.ColData?.[1]?.value;
      if (sLabel === label && sVal !== undefined) return parseFloat(sVal);
      const dLabel = row.ColData?.[0]?.value;
      const dVal   = row.ColData?.[1]?.value;
      if (dLabel === label && dVal !== undefined) return parseFloat(dVal);
      if (row.Rows?.Row) {
        const r = walk(row.Rows.Row);
        if (r !== null) return r;
      }
    }
    return null;
  };
  return walk(report.Rows.Row) ?? 0;
}

function sumLastColumn(report: IntuitReportResponse | undefined): number {
  if (!report?.Rows?.Row) return 0;
  let sum = 0;
  const walk = (rows: IntuitReportRow[]) => {
    for (const row of rows) {
      if (row.type === 'Data' && row.ColData && row.ColData.length > 0) {
        const v = row.ColData[row.ColData.length - 1]?.value;
        if (v) sum += parseFloat(v) || 0;
      }
      if (row.Rows?.Row) walk(row.Rows.Row);
    }
  };
  walk(report.Rows.Row);
  return sum;
}

function countDataRows(report: IntuitReportResponse | undefined): number {
  if (!report?.Rows?.Row) return 0;
  let n = 0;
  const walk = (rows: IntuitReportRow[]) => {
    for (const row of rows) {
      if (row.type === 'Data') n++;
      if (row.Rows?.Row) walk(row.Rows.Row);
    }
  };
  walk(report.Rows.Row);
  return n;
}

// ─── Main ────────────────────────────────────────────────────────────────

export default function FinancialDashboardInteractive() {
  const { currentUser } = useAuth();

  // Report state
  const [pl,     setPL]     = useState<ReportState<QBProfitLossReport>>(emptyState());
  const [ar,     setAR]     = useState<ReportState<QBARAgingDetailReport>>(emptyState());
  const [ap,     setAP]     = useState<ReportState<QBAPAgingDetailReport>>(emptyState());
  const [bs,     setBS]     = useState<ReportState<QBBalanceSheetReport>>(emptyState());
  const [cf,     setCF]     = useState<ReportState<QBCashFlowReport>>(emptyState());
  const [topC,   setTopC]   = useState<ReportState<QBSalesByCustomerReport>>(emptyState());
  const [topP,   setTopP]   = useState<ReportState<QBSalesByProductReport>>(emptyState());

  const [period, setPeriod] = useState<PLPeriod>('MTD');
  const [addRebateOpen, setAddRebateOpen] = useState(false);

  // AbortController refs
  const plAbort   = useRef<AbortController | null>(null);
  const arAbort   = useRef<AbortController | null>(null);
  const apAbort   = useRef<AbortController | null>(null);
  const bsAbort   = useRef<AbortController | null>(null);
  const cfAbort   = useRef<AbortController | null>(null);
  const topCAbort = useRef<AbortController | null>(null);
  const topPAbort = useRef<AbortController | null>(null);

  // AI briefing state
  const [aiBriefing, setAiBriefing] = useState<string | null>(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const briefingFetched = useRef(false);
  const { response: chatResponse, isLoading: chatLoading, sendMessage } = useChat('GEMINI', 'gemini/gemini-2.5-flash', false);

  // ── Fetch callbacks ────────────────────────────────────────────────────
  const fetchPL = useCallback(async () => {
    plAbort.current?.abort();
    const ctl = new AbortController();
    plAbort.current = ctl;
    setPL((s) => ({ ...s, loading: true, error: null }));
    try {
      const { start_date, end_date } = periodDates(period);
      const data = await fetchQBReport<QBProfitLossReport>(`/api/quickbooks/profit-loss?start_date=${start_date}&end_date=${end_date}`, ctl.signal);
      if (ctl.signal.aborted) return;
      setPL({ data, loading: false, error: null });
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      setPL({ data: null, loading: false, error: err instanceof Error ? err.message : String(err) });
    }
  }, [period]);

  const fetchAR = useCallback(async () => {
    arAbort.current?.abort();
    const ctl = new AbortController();
    arAbort.current = ctl;
    setAR((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await fetchQBReport<QBARAgingDetailReport>(`/api/quickbooks/ar-aging-detail?as_of_date=${isoToday()}`, ctl.signal);
      if (ctl.signal.aborted) return;
      setAR({ data, loading: false, error: null });
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      setAR({ data: null, loading: false, error: err instanceof Error ? err.message : String(err) });
    }
  }, []);

  const fetchAP = useCallback(async () => {
    apAbort.current?.abort();
    const ctl = new AbortController();
    apAbort.current = ctl;
    setAP((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await fetchQBReport<QBAPAgingDetailReport>(`/api/quickbooks/ap-aging-detail?as_of_date=${isoToday()}`, ctl.signal);
      if (ctl.signal.aborted) return;
      setAP({ data, loading: false, error: null });
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      setAP({ data: null, loading: false, error: err instanceof Error ? err.message : String(err) });
    }
  }, []);

  const fetchBS = useCallback(async () => {
    bsAbort.current?.abort();
    const ctl = new AbortController();
    bsAbort.current = ctl;
    setBS((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await fetchQBReport<QBBalanceSheetReport>(`/api/quickbooks/balance-sheet?as_of_date=${isoToday()}`, ctl.signal);
      if (ctl.signal.aborted) return;
      setBS({ data, loading: false, error: null });
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      setBS({ data: null, loading: false, error: err instanceof Error ? err.message : String(err) });
    }
  }, []);

  const fetchCF = useCallback(async () => {
    cfAbort.current?.abort();
    const ctl = new AbortController();
    cfAbort.current = ctl;
    setCF((s) => ({ ...s, loading: true, error: null }));
    try {
      const yStart = `${new Date().getUTCFullYear()}-01-01`;
      const data = await fetchQBReport<QBCashFlowReport>(`/api/quickbooks/cash-flow?start_date=${yStart}&end_date=${isoToday()}`, ctl.signal);
      if (ctl.signal.aborted) return;
      setCF({ data, loading: false, error: null });
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      setCF({ data: null, loading: false, error: err instanceof Error ? err.message : String(err) });
    }
  }, []);

  const fetchTopC = useCallback(async () => {
    topCAbort.current?.abort();
    const ctl = new AbortController();
    topCAbort.current = ctl;
    setTopC((s) => ({ ...s, loading: true, error: null }));
    try {
      const yStart = `${new Date().getUTCFullYear()}-01-01`;
      const data = await fetchQBReport<QBSalesByCustomerReport>(`/api/quickbooks/top-customers?start_date=${yStart}&end_date=${isoToday()}&top_n=10`, ctl.signal);
      if (ctl.signal.aborted) return;
      setTopC({ data, loading: false, error: null });
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      setTopC({ data: null, loading: false, error: err instanceof Error ? err.message : String(err) });
    }
  }, []);

  const fetchTopP = useCallback(async () => {
    topPAbort.current?.abort();
    const ctl = new AbortController();
    topPAbort.current = ctl;
    setTopP((s) => ({ ...s, loading: true, error: null }));
    try {
      const yStart = `${new Date().getUTCFullYear()}-01-01`;
      const data = await fetchQBReport<QBSalesByProductReport>(`/api/quickbooks/top-products?start_date=${yStart}&end_date=${isoToday()}&top_n=10`, ctl.signal);
      if (ctl.signal.aborted) return;
      setTopP({ data, loading: false, error: null });
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      setTopP({ data: null, loading: false, error: err instanceof Error ? err.message : String(err) });
    }
  }, []);

  // ── Bootstrap: stagger 7 fetches by 150ms each ────────────────────────
  // Per CLAUDE.md: parallel Promise.all of 5+ queries gets 429'd on cold
  // connections. 150ms staggers spread the load.
  useEffect(() => {
    let cancelled = false;
    const fetches = [fetchPL, fetchAR, fetchAP, fetchBS, fetchCF, fetchTopC, fetchTopP];
    (async () => {
      for (let i = 0; i < fetches.length; i++) {
        if (cancelled) return;
        if (i > 0) await new Promise((r) => setTimeout(r, 150));
        if (cancelled) return;
        fetches[i]();
      }
    })();
    return () => {
      cancelled = true;
      plAbort.current?.abort();
      arAbort.current?.abort();
      apAbort.current?.abort();
      bsAbort.current?.abort();
      cfAbort.current?.abort();
      topCAbort.current?.abort();
      topPAbort.current?.abort();
    };
  // Bootstrap fires once on mount; subsequent period changes are handled
  // by the dedicated effect below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // P&L period change → refetch only that report.
  useEffect(() => {
    fetchPL();
  }, [period, fetchPL]);

  // ── AI Briefing ────────────────────────────────────────────────────────
  const generateAIBriefing = useCallback(async () => {
    if (briefingFetched.current) return;
    // Wait until P&L + AR + AP + cash flow have data (real or mock).
    if (!pl.data || !ar.data || !ap.data || !cf.data) return;
    briefingFetched.current = true;

    try {
      const cached = sessionStorage.getItem(BRIEFING_CACHE_KEY);
      if (cached) {
        const { text, ts } = JSON.parse(cached);
        if (Date.now() - ts < 30 * 60 * 1000) {
          setAiBriefing(text);
          return;
        }
      }
    } catch { /* ignore */ }

    setBriefingLoading(true);

    const revenue   = findRowValue(pl.data.data, 'Total Income');
    const cogs      = findRowValue(pl.data.data, 'Total COGS');
    const grossProf = findRowValue(pl.data.data, 'Gross Profit');
    const netIncome = findRowValue(pl.data.data, 'Net Income');
    const arTotal   = sumLastColumn(ar.data.data);
    const arCount   = countDataRows(ar.data.data);
    const apTotal   = sumLastColumn(ap.data.data);
    const apCount   = countDataRows(ap.data.data);
    const operating = findRowValue(cf.data.data, 'Net Cash from Operating');

    const dataContext = `
Live Financial Snapshot for NextRoof 360 (${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}):
- P&L ${period}: Revenue $${revenue.toLocaleString()}, COGS $${cogs.toLocaleString()}, Gross Profit $${grossProf.toLocaleString()}, Net Income $${netIncome.toLocaleString()}
- Accounts Receivable: $${arTotal.toLocaleString()} outstanding across ${arCount} invoices
- Accounts Payable: $${apTotal.toLocaleString()} due across ${apCount} bills
- Cash from Operating activities (YTD): $${operating.toLocaleString()}
- Bookkeeper/CFO name: ${currentUser.first_name}
- Data sources: ${pl.data.mock ? 'MOCK (no QB connection)' : 'live QuickBooks'}
    `.trim();

    sendMessage([
      {
        role: 'system',
        content: 'You are SmartPick™, an AI financial analyst for NextRoof 360, a roofing company. The reader is a bookkeeper or CFO doing daily accounting work — frame insights as "act on this today," not strategic narrative. Generate a concise 3-5 sentence financial summary based on the live QuickBooks data provided. Highlight cash position, overdue AR patterns, upcoming AP risk, and unit economics. Include 1-2 specific actionable recommendations the bookkeeper can take this week. Be direct and data-driven. Write as flowing prose, no bullet points or headers.',
      },
      { role: 'user', content: dataContext },
    ], { max_completion_tokens: 320 });
  }, [pl.data, ar.data, ap.data, cf.data, period, currentUser.first_name, sendMessage]);

  useEffect(() => {
    generateAIBriefing();
  }, [generateAIBriefing]);

  useEffect(() => {
    if (chatResponse && !chatLoading) {
      setAiBriefing(chatResponse);
      setBriefingLoading(false);
      try {
        sessionStorage.setItem(BRIEFING_CACHE_KEY, JSON.stringify({ text: chatResponse, ts: Date.now() }));
      } catch { /* ignore */ }
    }
  }, [chatResponse, chatLoading]);

  const handleRefreshBriefing = () => {
    briefingFetched.current = false;
    setAiBriefing(null);
    try { sessionStorage.removeItem(BRIEFING_CACHE_KEY); } catch { /* ignore */ }
    generateAIBriefing();
  };

  const isBriefingLoading = briefingLoading || chatLoading;

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F9FA', fontFamily: "'Inter', 'Poppins', sans-serif" }}>
      <div className="sticky top-[60px] z-30 border-b" style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <div className="flex items-center justify-between px-6 py-3.5">
          <div>
            <h1 className="font-bold text-xl" style={{ color: '#000150' }}>Financial Hub</h1>
            <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
              CFO Command Center · real-time financial snapshot
            </p>
          </div>
          <div className="flex items-center gap-3">
            <QuickBooksStatusButton />

            <button
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold transition-all hover:opacity-90 active:scale-95"
              style={{ backgroundColor: '#033494' }}
              suppressHydrationWarning
            >
              <Icon name="ArrowDownTrayIcon" size={16} />
              Export CSV/QuickBooks
            </button>

            <button
              className="relative w-9 h-9 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
              suppressHydrationWarning
            >
              <Icon name="BellIcon" size={18} className="text-gray-600" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" />
            </button>

            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #07B0E9 0%, #033494 100%)' }}
              suppressHydrationWarning
            >
              {currentUser?.first_name?.charAt(0)}{currentUser?.last_name?.charAt(0)}
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 space-y-6">
        <Row1Hero
          userName={currentUser.first_name}
          aiBriefing={aiBriefing}
          briefingLoading={isBriefingLoading}
          onRefreshBriefing={handleRefreshBriefing}
        />
        <Row2KPIStrip balanceSheet={bs} arAging={ar} apAging={ap} profitLoss={pl} />
        <Row3PLThisMonth profitLoss={pl} period={period} onPeriodChange={setPeriod} onRetry={fetchPL} />
        <Row4ARAPDetail arAging={ar} apAging={ap} onRetryAR={fetchAR} onRetryAP={fetchAP} />
        <Row5CashFlowBalanceSheet cashFlow={cf} balanceSheet={bs} onRetryCash={fetchCF} onRetryBS={fetchBS} />
        <Row5_5MaterialRebates onAddRebate={() => setAddRebateOpen(true)} />
        <Row6TopPerformers topCustomers={topC} topProducts={topP} onRetryCustomers={fetchTopC} onRetryProducts={fetchTopP} />
      </div>
      <AddRebateDrawer open={addRebateOpen} onClose={() => setAddRebateOpen(false)} />
    </div>
  );
}
